/**
 * Tier Utilities
 * 
 * Centralized tier checking and feature gating logic.
 * Ensures consistent tier precedence and feature access across the app.
 */

// ═══════════════════════════════════════════════════════════════════════════════
// TIER DEFINITIONS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Tier hierarchy (lowest to highest)
 */
export const TIER_HIERARCHY = ['free', 'smart', 'enhanced', 'miq', 'genius', 'genius_plaite_monthly'];

/**
 * Tier aliases - maps various tier names to their canonical form
 */
export const TIER_ALIASES = {
  'free': 'free',
  'smart': 'smart',
  'enhanced': 'smart',
  'smart_tester': 'smart',
  'miq': 'genius',
  'genius': 'genius',
  'genius_tester': 'genius',
  'genius_plaite_monthly': 'genius',
  'genius_plaite_annual': 'genius'
};

/**
 * Normalize tier name to canonical form
 */
export const normalizeTier = (tier) => {
  if (!tier) return 'free';
  const lowerTier = tier.toLowerCase();
  return TIER_ALIASES[lowerTier] || lowerTier;
};

// ═══════════════════════════════════════════════════════════════════════════════
// TIER CHECKING FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Check if user is on free tier
 */
export const isFreeTier = (userTier) => {
  const normalized = normalizeTier(userTier);
  return normalized === 'free';
};

/**
 * Check if user has Smart tier or higher
 */
export const isSmartOrHigher = (userTier) => {
  const normalized = normalizeTier(userTier);
  return normalized === 'smart' || normalized === 'genius';
};

/**
 * Check if user has Genius tier (highest)
 */
export const isGeniusTier = (userTier) => {
  const normalized = normalizeTier(userTier);
  return normalized === 'genius';
};

/**
 * Check if user is premium (any paid tier)
 */
export const isPremiumTier = (userTier) => {
  return !isFreeTier(userTier);
};

/**
 * Get tier level (0 = free, 1 = smart, 2 = genius)
 */
export const getTierLevel = (userTier) => {
  const normalized = normalizeTier(userTier);
  switch (normalized) {
    case 'genius': return 2;
    case 'smart': return 1;
    default: return 0;
  }
};

/**
 * Check if userTier has access to a feature requiring minTier
 */
export const hasTierAccess = (userTier, minTier) => {
  return getTierLevel(userTier) >= getTierLevel(minTier);
};

// ═══════════════════════════════════════════════════════════════════════════════
// FEATURE ACCESS CHECKS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Feature-specific access checks
 * Returns true if user has access to the feature
 */
export const hasFeatureAccess = {
  // Basic features (free tier)
  mealLogging: () => true,
  basicDashboard: () => true,
  mealHistory: () => true,
  
  // Smart tier features
  smartMeals: (userTier) => isSmartOrHigher(userTier),
  smartPicks: (userTier) => isSmartOrHigher(userTier),
  weeklyBrief: (userTier) => isSmartOrHigher(userTier),
  miqBaseline: (userTier) => isSmartOrHigher(userTier),
  
  // Genius tier features
  smartGoals: (userTier) => isGeniusTier(userTier),
  customTargets: (userTier) => isGeniusTier(userTier),
  homeSwapRecipes: (userTier) => isGeniusTier(userTier),
  comboCustomizer: (userTier) => isGeniusTier(userTier),
  smartChoices: (userTier) => isGeniusTier(userTier),
  miqFullAccess: (userTier) => isGeniusTier(userTier),
  weeklyMealPrep: (userTier) => isGeniusTier(userTier),
  advancedMiqInsights: (userTier) => isGeniusTier(userTier)
};

// ═══════════════════════════════════════════════════════════════════════════════
// TIER DISPLAY HELPERS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Get display name for tier
 */
export const getTierDisplayName = (userTier) => {
  const normalized = normalizeTier(userTier);
  switch (normalized) {
    case 'genius': return 'Genius Plaite';
    case 'smart': return 'Smart Plaite';
    default: return 'Free';
  }
};

/**
 * Get tier badge color
 */
export const getTierColor = (userTier) => {
  const normalized = normalizeTier(userTier);
  switch (normalized) {
    case 'genius': return '#667eea'; // Purple
    case 'smart': return '#38ef7d';  // Green
    default: return '#888888';       // Gray
  }
};

/**
 * Get tier icon/emoji
 */
export const getTierEmoji = (userTier) => {
  const normalized = normalizeTier(userTier);
  switch (normalized) {
    case 'genius': return '🧠';
    case 'smart': return '⚡';
    default: return '🆓';
  }
};

export default {
  // Tier definitions
  TIER_HIERARCHY,
  TIER_ALIASES,
  normalizeTier,
  
  // Tier checks
  isFreeTier,
  isSmartOrHigher,
  isGeniusTier,
  isPremiumTier,
  getTierLevel,
  hasTierAccess,
  
  // Feature access
  hasFeatureAccess,
  
  // Display helpers
  getTierDisplayName,
  getTierColor,
  getTierEmoji
};
